// BodyClipLoader — streams remaining body clips into the live mixer after
// the avatar is ready, and lets the render loop pull a specific clip to the
// front of the queue on demand.
//
// At character load AvatarModel registers a small priority set (idle/breathing/
// fidget — see priorityClips.js) before flipping __AVATAR_READY__, then hands
// the remaining clips (including failed priority clips) to a loader here. It
// drains a few at a time during idle frames (requestIdleCallback). When the graph
// requests a clip that isn't loaded yet — an SM transition to listening/thinking/talking, or a
// gesture — GraphEvaluator / GestureChannel call request(name) to jump it to the
// front so it's available within a tick or two instead of waiting out the queue.
//
// All state is plain Maps/Sets/arrays — no React state, no per-frame allocation.
// Failed loads warn and retry only on demand after backoff, up to maxAttempts.
// They stay managed but do not count towards remaining(), even during retries.
// request() in steady state is a Map.has + early return.

/**
 * @param {object} opts
 * @param {Array<{name:string}>} opts.entries  remaining clip entries (including failed priority clips) to load.
 * @param {(entry) => Promise<any>} opts.loadAndRegister  loads one entry's GLB and registers it on the mixer.
 * @param {(cb: Function) => void} opts.schedule  schedules a drain tick (requestIdleCallback / setTimeout).
 * @param {number} [opts.batchSize]  clips to kick off per drain tick (default 2).
 * @param {number} [opts.maxConcurrent]  cap on simultaneous in-flight background
 *   loads (default Infinity). Bounds bandwidth so the background sweep doesn't
 *   starve an on-demand pull (which bypasses the cap). A character with dozens of
 *   clips otherwise fires dozens of parallel fetches that split the pipe and make
 *   the one clip a user is actually waiting on load slowly.
 * @param {() => number} [opts.now]  clock in milliseconds (default Date.now).
 * @param {number[]} [opts.retryDelaysMs]  backoff after each failure (default [5000, 20000]).
 * @param {number} [opts.maxAttempts]  maximum load attempts per clip (default 3).
 * @param {(...args) => void} [opts.warn]  failure logger (default console.warn).
 */
export function createBodyClipLoader({ entries, loadAndRegister, schedule, batchSize = 2, maxConcurrent = Infinity,
  now = () => Date.now(), retryDelaysMs = [5000, 20000], maxAttempts = 3, warn = (...args) => console.warn(...args) }) {
  const pending = new Map()        // name -> entry (not yet started)
  const queue = []                 // ordered list of pending names (front = next)
  const loading = new Set()        // names whose load is in flight
  const loaded = new Set()         // names fully registered
  const failed = new Set()         // names that exhausted their attempts
  const attempts = new Map()       // name -> number of failed attempts
  const retryAt = new Map()        // name -> earliest on-demand retry time
  const waiters = new Map()        // name -> [resolve, …] for ensure()
  let started = false
  let draining = false
  let paused = false               // background sweep held; on-demand pulls unaffected

  for (const e of entries ?? []) {
    if (!e?.name || pending.has(e.name)) continue
    pending.set(e.name, e)
    queue.push(e.name)
  }

  function beginLoad(name) {
    const entry = pending.get(name)
    if (!entry) return
    pending.delete(name)
    loading.add(name)
    const attempt = (attempts.get(name) ?? 0) + 1
    let p
    try { p = loadAndRegister(entry) } catch (err) { p = Promise.reject(err) }
    Promise.resolve(p)
      .then(() => loaded.add(name), err => {
        attempts.set(name, attempt)
        warn(`[BodyClipLoader] clip "${name}" failed to load (attempt ${attempt}/${maxAttempts}): ${err?.message || err}`)
        if (attempt < maxAttempts) {
          pending.set(name, entry)
          retryAt.set(name, now() + retryDelaysMs[attempt - 1])
        } else {
          failed.add(name)
          warn(`[BodyClipLoader] clip "${name}" gave up after ${attempt} attempts`)
        }
      })
      .finally(() => {
        loading.delete(name)
        const ws = waiters.get(name)
        if (ws) { waiters.delete(name); for (const r of ws) r(loaded.has(name)) }
        // A background slot freed — keep draining if the cap was the limiter.
        // Not while paused: an on-demand completion must not restart the sweep.
        if (started && !paused && queue.length > 0) scheduleDrain()
      })
  }

  // Pull a clip to the front of the queue and start it now. No-op if already
  // loading / loaded / failed / in backoff / not ours. Safe to call every frame.
  function request(name) {
    if (!pending.has(name)) return // loading / loaded / failed / not ours
    if (loading.has(name) || now() < (retryAt.get(name) ?? -Infinity)) return
    const i = queue.indexOf(name)
    if (i >= 0) queue.splice(i, 1)
    beginLoad(name)
  }

  function drain() {
    draining = false
    if (paused) return // a tick scheduled before pause() lands here — drop it
    let kicked = 0
    while (queue.length > 0 && kicked < batchSize && loading.size < maxConcurrent) {
      const name = queue.shift()
      if (!pending.has(name)) continue // already pulled on demand
      beginLoad(name)
      kicked++
    }
    // Reschedule only if more work remains AND we still have capacity (this tick
    // hit batchSize). If we stopped because the concurrency cap is full, the
    // beginLoad completion handler resumes draining when a slot frees — avoids an
    // idle busy-loop hammering requestIdleCallback while every slot is in use.
    if (queue.length > 0 && loading.size < maxConcurrent) scheduleDrain()
  }

  function scheduleDrain() {
    if (draining) return
    draining = true
    schedule(drain)
  }

  return {
    /** Begin background draining. Idempotent. */
    start() {
      if (started) return
      started = true
      if (queue.length > 0 && !paused) scheduleDrain()
    },

    /**
     * Hold the BACKGROUND sweep: no new background loads start; loads already
     * in flight complete normally. On-demand pulls (request / ensure) are
     * deliberately unaffected — the graph asking for a clip still gets it.
     * Exists for the e2e fixtures (settleForUi): a UI spec needs the GLB
     * parse work to stop competing for the main thread, not the clips loaded.
     * Idempotent.
     */
    pause() { paused = true },

    /** Resume background draining after pause(). Idempotent. */
    resume() {
      if (!paused) return
      paused = false
      if (started && queue.length > 0) scheduleDrain()
    },

    /** Count of loads currently in flight (excludes pending). The observable
     *  settleForUi waits on after pause() — zero means the main thread is no
     *  longer being stalled by background GLB parses. */
    inFlight() { return loading.size },

    /**
     * Pull a clip to the front of the queue and start it immediately. No-op if
     * the clip is loading, loaded, failed, in backoff, or unknown to this loader.
     * Cheap and idempotent — safe to call every frame from the render loop.
     */
    request,

    /**
     * Request a clip and resolve once it's registered (or already is). Resolves
     * `false` for unknown, failed, or backoff clips, and when a load fails.
     * For one-shot triggers (prefab playBodyClip) that need the action present
     * before they can act.
     */
    ensure(name) {
      if (loaded.has(name)) return Promise.resolve(true)
      if (!pending.has(name) && !loading.has(name)) return Promise.resolve(false)
      if (!loading.has(name) && now() < (retryAt.get(name) ?? -Infinity)) return Promise.resolve(false)
      request(name)
      return new Promise(resolve => {
        const arr = waiters.get(name) ?? []
        arr.push(resolve)
        waiters.set(name, arr)
      })
    },

    isPending(name) { return pending.has(name) },
    has(name) { return loaded.has(name) },
    /** True once the clip has exhausted its load attempts. */
    hasFailed(name) { return failed.has(name) },
    /** True if this loader owns the clip in any state, including backoff / failed. */
    isManaged(name) { return pending.has(name) || loading.has(name) || loaded.has(name) || failed.has(name) },
    /** Count of pending + in-flight clips that have never failed. */
    remaining() {
      let count = 0
      for (const name of pending.keys()) if (!attempts.has(name)) count++
      for (const name of loading) if (!attempts.has(name)) count++
      return count
    },
  }
}
